import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import EventSeatIcon from "@mui/icons-material/EventSeat";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import {
  Alert,
  Box,
  Breadcrumbs,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchDashboardOverview,
  selectDashboardData,
  selectDashboardError,
  selectDashboardLoading,
  selectDashboardTimeRange,
  setDashboardTimeRange,
} from "../store/slices/dashboardSlice";

const chartColors = ["#0f766e", "#f59e0b", "#dc2626", "#7c3aed", "#2563eb", "#ec4899"];

const formatNumber = (value) => new Intl.NumberFormat("en-US").format(value ?? 0);

const KpiCard = ({ label, value, subtitle, icon, tone }) => (
  <Card
    sx={{
      height: "100%",
      border: "1px solid rgba(148, 163, 184, 0.16)",
      boxShadow: "0px 18px 36px rgba(15, 23, 42, 0.06)",
      background: `linear-gradient(135deg, ${tone} 0%, rgba(255,255,255,0.98) 72%)`,
    }}
  >
    <CardContent>
      <Stack direction="row" justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="h4" sx={{ mt: 1, fontWeight: 800 }}>
            {formatNumber(value)}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {subtitle}
          </Typography>
        </Box>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 3,
            display: "grid",
            placeItems: "center",
            backgroundColor: "rgba(15, 23, 42, 0.08)",
            color: "#0f172a",
          }}
        >
          {icon}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const DonutChart = ({ items }) => {
  const total = items.reduce((sum, item) => sum + item.value, 0);
  const gradient = useMemo(() => {
    let current = 0;
    return items
      .map((item) => {
        const start = current;
        const slice = total === 0 ? 0 : (item.value / total) * 100;
        current += slice;
        return `${item.color} ${start}% ${current}%`;
      })
      .join(", ");
  }, [items, total]);

  return (
    <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="center">
      <Box
        sx={{
          width: 220,
          height: 220,
          borderRadius: "50%",
          background: `conic-gradient(${gradient})`,
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
          boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.35)",
        }}
      >
        <Box
          sx={{
            width: 122,
            height: 122,
            borderRadius: "50%",
            backgroundColor: "#ffffff",
            display: "grid",
            placeItems: "center",
            textAlign: "center",
            boxShadow: "0 10px 28px rgba(15, 23, 42, 0.08)",
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              Total Tickets
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {formatNumber(total)}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Stack spacing={1.5} sx={{ width: "100%" }}>
        {items.map((item) => {
          const percent = total === 0 ? 0 : Math.round((item.value / total) * 100);
          return (
            <Stack
              key={item.label}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                p: 1.5,
                borderRadius: 2,
                backgroundColor: "rgba(248, 250, 252, 0.9)",
                border: "1px solid rgba(148, 163, 184, 0.16)",
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    backgroundColor: item.color,
                  }}
                />
                <Typography fontWeight={600}>{item.label}</Typography>
              </Stack>
              <Stack direction="row" spacing={1.5}>
                <Typography color="text.secondary">{percent}%</Typography>
                <Typography fontWeight={700}>{formatNumber(item.value)}</Typography>
              </Stack>
            </Stack>
          );
        })}
      </Stack>
    </Stack>
  );
};

const HorizontalBarChart = ({ items }) => {
  const maxValue = Math.max(...items.map((item) => item.usage), 1);
  return (
    <Stack spacing={2}>
      {items.map((item, index) => (
        <Box key={item.event}>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
            <Typography fontWeight={600}>{item.event}</Typography>
            <Typography color="text.secondary">{formatNumber(item.usage)} uses</Typography>
          </Stack>
          <Box
            sx={{
              height: 14,
              borderRadius: 999,
              backgroundColor: "rgba(226, 232, 240, 0.75)",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                width: `${(item.usage / maxValue) * 100}%`,
                height: "100%",
                borderRadius: 999,
                background:
                  index % 2 === 0
                    ? "linear-gradient(90deg, #0f766e 0%, #14b8a6 100%)"
                    : "linear-gradient(90deg, #1d4ed8 0%, #60a5fa 100%)",
              }}
            />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
            {item.share}% of ticket usage
          </Typography>
        </Box>
      ))}
    </Stack>
  );
};

const DashboardPage = () => {
  const dispatch = useDispatch();
  const timeRange = useSelector(selectDashboardTimeRange);
  const data = useSelector(selectDashboardData);
  const loading = useSelector(selectDashboardLoading);
  const error = useSelector(selectDashboardError);

  useEffect(() => {
    dispatch(fetchDashboardOverview(timeRange));
  }, [dispatch, timeRange]);

  const summary = data?.summary || {
    totalTickets: 0,
    totalTaps: 0,
    tapIn: 0,
    tapOut: 0,
  };

  const ticketUsage = useMemo(() => {
    const items = data?.ticketUsageDistribution?.items || [];
    return items.map((item, index) => ({
      label: item.label,
      value: item.ticketCount ?? 0,
      color: chartColors[index % chartColors.length],
    }));
  }, [data]);

  const eventUsage = useMemo(() => {
    const items = data?.eventUsage?.items || [];
    return items.map((item) => ({
      event: item.event_name,
      usage: item.usageCount ?? 0,
      share: item.sharePercent ?? 0,
    }));
  }, [data]);

  const stationFlow = data?.stationFlow?.items || [];

  const stationMax = Math.max(
    ...stationFlow.map((station) => Math.max(station.tapIn ?? 0, station.tapOut ?? 0)),
    1
  );

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", md: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
        }}
      >
        <Box>
          <Breadcrumbs
            aria-label="breadcrumb"
            separator=">"
            sx={{ fontSize: 12, mb: 1, color: "text.secondary" }}
          >
            <Link underline="hover" color="inherit" href="#">
              Dashboard
            </Link>
            <Typography color="text.primary">Overview</Typography>
          </Breadcrumbs>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Operations Dashboard
          </Typography>
          <Typography color="text.secondary">
            Quick read on ticket usage, station throughput, and event performance.
          </Typography>
        </Box>
        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel id="dashboard-time-range-label">Time Range</InputLabel>
          <Select
            labelId="dashboard-time-range-label"
            value={timeRange}
            label="Time Range"
            onChange={(event) => dispatch(setDashboardTimeRange(event.target.value))}
          >
            <MenuItem value="yesterday">Yesterday</MenuItem>
            <MenuItem value="today">Today</MenuItem>
            <MenuItem value="thisWeek">This Week</MenuItem>
            <MenuItem value="thisMonth">This Month</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {loading && <LinearProgress />}
      {error && <Alert severity="error">{error}</Alert>}

      <Grid
        container
        rowSpacing={2.5}
        columnSpacing={2.5}
        sx={{ width: "100%", m: 0 }}
      >
        <Grid item xs={12} sm={6} lg={3}>
          <KpiCard
            label="Total Tickets"
            value={summary.totalTickets}
            subtitle="Tickets matched to the selected period."
            icon={<DashboardCustomizeIcon />}
            tone="rgba(20, 184, 166, 0.18)"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <KpiCard
            label="Total Taps"
            value={summary.totalTaps}
            subtitle="Combined tap activity across all monitored stations."
            icon={<EventSeatIcon />}
            tone="rgba(37, 99, 235, 0.18)"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <KpiCard
            label="Tap In"
            value={summary.tapIn}
            subtitle="Passengers entering through the special lane."
            icon={<LoginIcon />}
            tone="rgba(245, 158, 11, 0.18)"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <KpiCard
            label="Tap Out"
            value={summary.tapOut}
            subtitle="Passengers exiting through the special lane."
            icon={<LogoutIcon />}
            tone="rgba(239, 68, 68, 0.18)"
          />
        </Grid>
      </Grid>

      <Grid
        container
        rowSpacing={3}
        columnSpacing={3}
        sx={{ width: "100%", m: 0 }}
      >
        <Grid item xs={12} lg={6}>
          <Paper
            sx={{
              p: { xs: 2, sm: 3 },
              height: "100%",
              boxShadow: "0px 24px 48px rgba(15, 23, 42, 0.05)",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              Ticket Usage Distribution
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Highlights unused inventory against tickets consumed once, twice, or more.
            </Typography>
            <DonutChart items={ticketUsage} />
          </Paper>
        </Grid>
        <Grid item xs={12} lg={6}>
          <Paper
            sx={{
              p: { xs: 2, sm: 3 },
              height: "100%",
              boxShadow: "0px 24px 48px rgba(15, 23, 42, 0.05)",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              Ticket Usage by Event
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Compares event consumption to show which programs are driving most usage.
            </Typography>
            <HorizontalBarChart items={eventUsage} />
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper
            sx={{
              p: { xs: 2, sm: 3 },
              boxShadow: "0px 24px 48px rgba(15, 23, 42, 0.05)",
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              spacing={1.5}
              sx={{ mb: 3 }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Station Tap In / Tap Out
                </Typography>
                <Typography color="text.secondary">
                  Each station shows inbound and outbound activity for the selected period.
                </Typography>
              </Box>
              <Chip
                label={loading ? "Refreshing..." : "Live Data"}
                color="primary"
                variant="outlined"
              />
            </Stack>

            <Grid container rowSpacing={3} columnSpacing={3} sx={{ width: "100%", m: 0 }}>
              <Grid item xs={12} lg={6}>
                <Stack spacing={2}>
                  {stationFlow.map((station) => (
                    <Box key={station.station_code || station.station_name}>
                      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
                        <Typography fontWeight={600}>
                          {station.station_name || station.station_code}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatNumber(station.total)} total taps
                        </Typography>
                      </Stack>
                      <Stack spacing={0.75}>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "68px 1fr 56px",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Tap In
                          </Typography>
                          <Box
                            sx={{
                              height: 12,
                              borderRadius: 999,
                              backgroundColor: "rgba(226, 232, 240, 0.75)",
                              overflow: "hidden",
                            }}
                          >
                            <Box
                              sx={{
                                width: `${((station.tapIn ?? 0) / stationMax) * 100}%`,
                                height: "100%",
                                borderRadius: 999,
                                background: "linear-gradient(90deg, #059669 0%, #34d399 100%)",
                              }}
                            />
                          </Box>
                          <Typography variant="body2" fontWeight={700}>
                            {formatNumber(station.tapIn)}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "68px 1fr 56px",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Tap Out
                          </Typography>
                          <Box
                            sx={{
                              height: 12,
                              borderRadius: 999,
                              backgroundColor: "rgba(226, 232, 240, 0.75)",
                              overflow: "hidden",
                            }}
                          >
                            <Box
                              sx={{
                                width: `${((station.tapOut ?? 0) / stationMax) * 100}%`,
                                height: "100%",
                                borderRadius: 999,
                                background: "linear-gradient(90deg, #1d4ed8 0%, #60a5fa 100%)",
                              }}
                            />
                          </Box>
                          <Typography variant="body2" fontWeight={700}>
                            {formatNumber(station.tapOut)}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Grid>
              <Grid item xs={12} lg={6}>
                <TableContainer
                  sx={{
                    border: "1px solid rgba(148, 163, 184, 0.16)",
                    borderRadius: 2,
                  }}
                >
                  <Table>
                    <TableHead
                      sx={{
                        background: "linear-gradient(90deg, #030712 0%, #0f172a 50%, #111827 100%)",
                        "& th": {
                          color: "#ffffff",
                          fontWeight: 600,
                          borderBottom: "none",
                        },
                      }}
                    >
                      <TableRow>
                        <TableCell>Station</TableCell>
                        <TableCell align="right">Tap In</TableCell>
                        <TableCell align="right">Tap Out</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stationFlow.map((station) => (
                        <TableRow
                          key={`table-${station.station_code || station.station_name}`}
                          hover
                        >
                          <TableCell>{station.station_name || station.station_code}</TableCell>
                          <TableCell align="right">{formatNumber(station.tapIn)}</TableCell>
                          <TableCell align="right">{formatNumber(station.tapOut)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            {formatNumber(station.total)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default DashboardPage;
